import { bookActions, deployActions } from '@/actions';
import type { BookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import { toSlugId } from '@/constants/pages';
import { consumeBookingFlowEntryScroll, enterBookingFlow } from '@/features/booking-flow/goToStep';
import { getFirstStep } from '@/features/booking-flow/steps';
import { useBookingFlowRedesign } from '@/features/booking-flow/useBookingFlowRedesign';
import { useCalendarOffersEmployeePick } from '@/features/booking-flow/useCalendarOffersEmployeePick';
import { exportIdSlug, isServer, server, setWhitelabelSession, updateDeployTimestamp } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setLang } from '@/locale';
import { languageSchema } from '@/locale/i18n';
import { EmployeeStats, Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { useEffect, useReducer, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import HighlightMatch from './components/HighLightMatch';
import { savePlaceVisit, trackPlacePage } from './helpers/tracking';
import placePageReducer from './PlacePage.reducer';
import { throttle } from 'lodash';

type activeTabs = 'employees' | 'reviews' | 'about' | 'services';

export const usePlacePage = (
  place: Place,
  headerTargetRef: React.MutableRefObject<any>,
  context: BookAppointmentContext,
  isSistaminuten: boolean,
  isWhitelabel: boolean,
) => {
  const location = useLocation<{ fromList?: boolean; searchParams?: Record<string, string> }>();
  const history = useHistory();
  const { slugId } = useParams() as { slugId: string };
  const appDispatch = useAppDispatch();
  const deploy = useAppSelector((state) => state.deploy);
  const user = useAppSelector((state) => state.users)?.user;
  const bookingFlowRedesign = useBookingFlowRedesign();
  const calendarOffersEmployeePick = useCalendarOffersEmployeePick();
  const [matchedServices, setMatchedServices] = useState<PlaceService[]>([]);
  const searchParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState<activeTabs>(undefined);

  const employeesTabRef = useRef<HTMLDivElement | null>(null);
  const reviewsTabRef = useRef<HTMLDivElement | null>(null);
  const aboutTabRef = useRef<HTMLDivElement | null>(null);
  const servicesTabRef = useRef<HTMLDivElement | null>(null);

  const [state, dispatch] = useReducer(placePageReducer, {
    search: '',
    showSearch: false,
    searchedServices: [],
    searchedBundles: [],
  });

  // `location.state` is only meaningful at entry (pushed by the search/deals result card).
  const entryLocationStateRef = useRef(location.state);

  const tabs = [
    { id: 'employees', ref: employeesTabRef },
    { id: 'reviews', ref: reviewsTabRef },
    { id: 'about', ref: aboutTabRef },
    { id: 'services', ref: servicesTabRef },
  ];

  const checkActiveTab = () => {
    const scrollY = window.scrollY || window.pageYOffset;

    tabs.forEach((tab) => {
      if (tab.ref?.current) {
        const { offsetTop, offsetHeight } = tab.ref.current;
        const currentTabBottom = offsetTop + offsetHeight;

        const menuHeight = 164.5;

        if (scrollY >= offsetTop - menuHeight && scrollY < currentTabBottom - menuHeight) {
          setActiveTab(tab.id as activeTabs);
        }
      }
    });
  };

  // eslint-disable-next-line
  const calculateActiveTab = useCallback(
    throttle(checkActiveTab, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    // Throttle to once per animation frame: native scroll events can fire far more often
    // than the UI can usefully react to, and the sticky check only needs to run per paint.
    let rafId: number | null = null;
    // useReducers same-state bailout skips re-rendering children, but React still has to
    // call this component's function to find that out — so redispatching the unchanged value
    // every frame was still re-running the whole component on every scroll tick. Tracking the
    // last dispatched value here means we only dispatch (and thus only re-render) on an actual
    // sticky/non-sticky transition.
    let lastIsSticky: boolean | null = null;

    const handleScroll = () => {
      // The lightbox locks the body with `position: fixed`, which drops the document scroll to
      // 0 and fires a scroll event. Recomputing off that would flip the header to its
      // top-of-page variant behind the overlay; the page can't move while locked, so the value
      // computed before the lock is still the right one.
      if (document.documentElement.dataset.scrollLocked) {
        return;
      }

      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        calculateActiveTab();

        rafId = null;
        if (headerTargetRef.current) {
          const scrollY = window.scrollY || window.pageYOffset;
          const { offsetTop } = headerTargetRef.current;
          const nextIsSticky = offsetTop - scrollY <= 64.5; // What is 64.5? It seems to be the threshold for when the header becomes sticky.
          if (nextIsSticky === lastIsSticky) {
            return;
          }
          lastIsSticky = nextIsSticky;
          dispatch({ type: 'SET_STICKY', payload: nextIsSticky });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // A scroll event between the last frame and unmount can leave a callback queued —
      // cancel it so it doesn't dispatch against an unmounted tree.
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const { id, slug } = exportIdSlug(slugId);

    setWhitelabelSession(isWhitelabel);

    if (searchParams.has('lang')) {
      const lang = languageSchema.safeParse(searchParams.get('lang'));
      if (lang.success) setLang(lang.data);
    }

    if (searchParams.has('gclid')) {
      sessionStorage.setItem('gclid', searchParams.get('gclid'));
    }

    sessionStorage.setItem('bookSPurl', `/places/${slug}-${id}`);
    sessionStorage.setItem('paymentSPurl', `/places/${slug}-${id}`);

    if (!isWhitelabel) {
      trackPlacePage(place);
    }

    if (!isServer) {
      savePlaceVisit(place);

      const lastVisitedPlaces = JSON.parse(localStorage.getItem('mpLastVisitedPlaces') || '[]');

      if (!lastVisitedPlaces.includes(place.id)) {
        if (lastVisitedPlaces.length >= 5) {
          lastVisitedPlaces.shift();
        }
        lastVisitedPlaces.push(place.id);
      }

      localStorage.setItem('mpLastVisitedPlaces', JSON.stringify(lastVisitedPlaces));
    }
    if (!isServer) {
      // scroll down to the section if there is a hash in the url, otherwise scroll to top
      const targetId = window.location.hash.slice(1);
      const hashTarget = targetId ? document.getElementById(targetId) : null;
      const entryScroll = consumeBookingFlowEntryScroll(place.id);
      if (hashTarget) {
        hashTarget.scrollIntoView();
      } else if (entryScroll != null) {
        // Returning from the booking flow (Avbryt or browser back): keep the user's position.
        window.scrollTo(0, entryScroll);
      } else {
        window.scrollTo(0, 0);
      }
      document.querySelector('html').classList.add('scroll-smooth');
    }

    if (deploy) {
      updateDeployTimestamp(deploy);
      appDispatch(deployActions.clearDeployTimestamp());
    }

    return () => {
      if (!isServer) {
        document.querySelector('html').classList.remove('scroll-smooth');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFirstAvailableTimeServices = async () => {
    const entryState = entryLocationStateRef.current;
    const shouldHaveFirstAvailableTimes =
      entryState?.fromList && entryState?.searchParams?.q && !(place && place.schedule);

    if (shouldHaveFirstAvailableTimes) {
      const searchParams = Object.fromEntries(Object.entries(entryState.searchParams).filter((param) => param));
      const placeId = place.id;

      const searchEndpoint = isSistaminuten ? '/search/last-minute' : '/search/new';

      const response = await server.request
        .get(searchEndpoint, { params: { ...searchParams, placeId: placeId } })
        .then(server.handleSuccess)
        .catch(server.handleErrorObject);

      const matchedServices = response?.places?.[0]?.matchedServices;

      if (!Array.isArray(matchedServices)) return null;

      const allServicesHash = {};
      place.services.forEach((category) => {
        if (category.services) {
          category.services.forEach((service) => {
            allServicesHash[service.id] = service;
          });
        }
      });

      const services = matchedServices
        .map((ms) => {
          if (ms.isBundle) {
            return ms;
          } else {
            return {
              ...ms,
              ...allServicesHash[ms.id],
            };
          }
        })
        .filter((s) => !!s);

      setMatchedServices(services);
    } else {
      return null;
    }
  };

  useEffect(() => {
    getFirstAvailableTimeServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveSearch = () => {
    dispatch({ type: 'REMOVE_SEARCH' });
  };

  const handleShowSearchInput = () => {
    dispatch({ type: 'SHOW_SEARCH_INPUT' });
  };

  const handleOnSearch = (event: React.ChangeEvent<HTMLInputElement>, { newValue }: { newValue?: string }) => {
    dispatch({ type: 'CHANGE_SEARCH_INPUT', value: newValue });
    if (newValue) {
      const filteredBundles = place.bookingBundles
        .filter((bundle) => {
          const bundleName = bundle.name.toLowerCase();
          const searchValue = newValue.toLowerCase();
          return bundleName.includes(searchValue);
        })
        .map((bundle) => {
          return { ...bundle, highlightedName: <HighlightMatch query={newValue} text={bundle.name} /> };
        });
      const filteredServices = place.services
        .flatMap((category) => category.services)
        .filter((service) => {
          const serviceName = service.name.toLowerCase();
          const searchValue = newValue.toLowerCase();

          return serviceName.includes(searchValue);
        })
        .map((service) => {
          return { ...service, highlightedName: <HighlightMatch query={newValue} text={service.name} /> };
        });

      dispatch({ type: 'CHANGE_SEARCHED_SERVICES', payload: { services: filteredServices, bundles: filteredBundles } });
    } else {
      dispatch({ type: 'CHANGE_SEARCHED_SERVICES', payload: { services: [], bundles: [] } });
    }
  };

  const handleOnEmployeeClick = (employee: PlaceEmployee, rating?: EmployeeStats[string]) => {
    dispatch({ type: 'SET_LEGACY_EMPLOYEE_REVIEWS', payload: { employee, rating } });
    return;
  };

  const handleOnCloseEmployeeReviews = () => {
    dispatch({ type: 'REMOVE_LEGACY_EMPLOYEE_REVIEWS' });
  };

  const handleOnPickEmployee = (employee: PlaceEmployee) => {
    if (bookingFlowRedesign.enabled) {
      appDispatch(bookActions.clearBook());
      appDispatch(bookActions.pickEmployee(employee.id, employee.about.priceListId));
      appDispatch(bookActions.keepEmployee());
      appDispatch(bookActions.addPlace(place));
      enterBookingFlow(
        history,
        toSlugId(place.about.slug, place.id),
        getFirstStep({ place, services: [], employee: employee.id, user, calendarOffersEmployeePick }),
        { sr: employee.about.externalId ?? null },
        { sistaminuten: isSistaminuten || Boolean(place?.lastMinuteDiscount) },
      );
      return;
    }
    context.updateView('pickService');
    appDispatch(bookActions.clearBook());
    appDispatch(bookActions.removeEmployee());
    appDispatch(bookActions.pickEmployee(employee.id, employee.about.priceListId));
    appDispatch(bookActions.keepEmployee());
    appDispatch(bookActions.addPlace(place));
    context.updateUrlState({ type: 'employee', externalId: employee.about.externalId });
    context.open();
  };

  return {
    matchedServices,
    activeTab,
    tabRefs: {
      reviewsTabRef,
      aboutTabRef,
      servicesTabRef,
      employeesTabRef,
    },
    onRemoveSearch: handleRemoveSearch,
    onShowSearchInput: handleShowSearchInput,
    onSearch: handleOnSearch,
    onEmployeeClick: handleOnEmployeeClick,
    onCloseLegacyEmployeeReviews: handleOnCloseEmployeeReviews,
    onPickEmployee: handleOnPickEmployee,
    ...state,
  };
};
